import {PropsWithChildren, createContext, useState} from 'react';

import {LAST_WORKSPACE_ID, LAST_WORKSPACE_IDS} from 'src/constants';
import {getDefaultWorkspace, getWorkspace} from 'src/api/workspace';
import {Workspace} from 'src/types';

type WorkspaceContextType = {
  currentWorkspace: Workspace | undefined,
  setCurrentWorkspace: (w: Workspace | undefined) => void,
  loading: boolean,
  initializeWorkspace: (id?: string) => Promise<void>
};

const INITIAL_WORKSPACE_PROPS = {
  currentWorkspace: undefined,
  loading: false,
  setCurrentWorkspace: () => {},
  initializeWorkspace: async () => {},
};

export const WorkspaceContext = createContext<WorkspaceContextType>(INITIAL_WORKSPACE_PROPS);

export const WorkspaceContextProvider = ({children}: PropsWithChildren<unknown>) => {
  const [loading, setLoading] = useState(false);
  const [currentWorkspace, internalSetCurrentWorkspace] = useState<Workspace>();
  const [workspaceHistory, setWorkspaceHistory] = useState<Set<string>>(() => {
    const historyStr = localStorage.getItem(LAST_WORKSPACE_IDS) || '[]';
    return new Set<string>(Array.from(JSON.parse(historyStr)));
  });

  const setCurrentWorkspace = (workspace?: Workspace) => {
    if (workspace) {
      const history = workspaceHistory;
      if (history.has(workspace.id)) {
        history.delete(workspace.id);
      }
      if (history.size > 3) {
        const [last] = Array.from(history);
        if (last) {
          history.delete(last);
        }
      }
      history.add(workspace.id);
      setWorkspaceHistory(history);
      localStorage.setItem(LAST_WORKSPACE_IDS, JSON.stringify(Array.from(history)));
    } else {
      localStorage.removeItem(LAST_WORKSPACE_ID);
    }

    internalSetCurrentWorkspace(workspace);
  };

  const initializeWorkspace = async (paramWorkspaceId?: string) => {
    let workspace: Workspace | undefined;
    setLoading(true);
    if (paramWorkspaceId) {
      workspace = await getWorkspaceByIdOrDefault(paramWorkspaceId);
    } else {
      const lastId = localStorage.getItem(LAST_WORKSPACE_ID) || undefined;
      workspace = await getWorkspaceFromHistory();
      if (!workspace) {
        workspace = await getWorkspaceByIdOrDefault(lastId);
      }
    }
    setLoading(false);
    setCurrentWorkspace(workspace);
  };

  const getWorkspaceByIdOrDefault = async (id: string | undefined): Promise<Workspace | undefined> => {
    const {data, status} = id ? await getWorkspace({id}) : await getDefaultWorkspace();
    if (status === 'success' && data) {
      return data;
    } else {
      return undefined;
    }
  };

  const getWorkspaceFromHistory = async (): Promise<Workspace | undefined> => {
    for (const value of Array.from(workspaceHistory).reverse()) {
      if (!value) continue;
      const {data, status} = await getWorkspace({id: value});
      if (status === 'success' && data) {
        return data;
      }
    }

    return undefined;
  };

  return (
    <WorkspaceContext.Provider value={{
      currentWorkspace,
      setCurrentWorkspace,
      initializeWorkspace,
      loading,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
